<script setup lang="ts">
import { useTranslation } from "i18next-vue";
import type { Report } from "@/types";
import DRDisplayItem from "@/components/UI/items/DRDisplayItem.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDropdownItem from "@/components/UI/DRDropdownItem.vue";
import TemplateIcon from "@/components/UI/icons/TemplateIcon.vue";

/**
 * Ligne de rapport, calquée sur l'item de plan (DRDisplayItem en mode ligne).
 * `current` = version en vigueur : simple badge, les autres sont estompées.
 */

withDefaults(
  defineProps<{
    report: Report;
    /** Nom affiché quand le rapport n'a pas de titre. */
    fallbackName?: string;
    current?: boolean;
  }>(),
  { current: false }
);

const emit = defineEmits<{
  (e: "open"): void;
  (e: "set-default"): void;
}>();

const { t } = useTranslation();

const formatReportDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
</script>

<template>
  <DRDisplayItem
    display="line"
    class="report-item"
    :class="{ muted: !current }"
    @click="emit('open')"
  >
    <template #icon="{ size }">
      <TemplateIcon :size="size" />
    </template>

    <template #title>
      <div class="d-flex align-items-center gap-2 text-truncate">
        <span class="text-truncate">
          {{
            report.name ||
            fallbackName ||
            t("report:diagnostics.untitledReport")
          }}
        </span>
        <span v-if="current" class="badge bg-primary flex-shrink-0">
          {{ t("report:diagnostics.defaultBadge") }}
        </span>
      </div>
    </template>

    <template #subtitle>
      <span v-if="report.template">{{ report.template.name }}</span>
      <span>{{ formatReportDate(report.createdAt) }}</span>
      <span v-if="report._count?.sections">
        <i class="bi-diagram-3 me-1" />{{
          t("report:diagnostics.reportSections", {
            count: report._count.sections,
          })
        }}
      </span>
      <span v-if="report._count?.findings">
        <i class="bi-exclamation-triangle me-1" />{{
          t("report:diagnostics.reportFindings", {
            count: report._count.findings,
          })
        }}
      </span>
    </template>

    <template #actions>
      <el-dropdown trigger="click" @click.stop.prevent>
        <DRBtnIcon
          icon="bi-three-dots"
          variant="flat"
          :size="30"
          @click.stop.prevent
        />
        <template #dropdown>
          <el-dropdown-menu>
            <DRDropdownItem
              icon="bi-pencil"
              :label="t('report:diagnostics.openReport')"
              @select="emit('open')"
            />
            <DRDropdownItem
              v-if="!current"
              icon="bi-star"
              :label="t('report:diagnostics.setAsDefault')"
              @select="emit('set-default')"
            />
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </template>
  </DRDisplayItem>
</template>

<style lang="scss" scoped>
/* Les versions qui ne font plus foi restent lisibles mais s'effacent. */
.report-item.muted {
  opacity: 0.65;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }
}
</style>
